<script lang="ts" setup>
import { OtIcon, OtLozenge, OtNumberDisplay } from '@1trading/design-system';
import { computed, type PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import type { NormalizedPerpsPosition } from '@exchange/libs/account/service/src/lib/wss-account-messages';

import usePositionComputed from '../../usePositionComputed';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
  position: { type: Object as PropType<NormalizedPerpsPosition>, required: true },
  maxLeverage: { type: Number, required: false },
  isCurrentMarketPosition: { type: Boolean, required: true },
});

const emit = defineEmits(['close-position']);

const { position, maxLeverage } = toRefs(props);
const { marginUsed, market } = usePositionComputed(position, maxLeverage);

const directionText = computed(() => (props.position.direction === 'LONG' ? t('fundamentals.orders.side.long') : t('fundamentals.orders.side.short')));

const handleClosePosition = () => {
  emit('close-position', props.position);
};
</script>

<template>
  <div class="open-positions-item-mobile">
    <div class="open-positions-item-mobile__header">
      <div class="open-positions-item-mobile__market-info">
        <span class="open-positions-item-mobile__market-name">{{ market?.name }}</span>
        <ot-lozenge
          :filled="false"
          :text="$t('fundamentals.perpetual.short')"
          class="open-positions-item-mobile__lozenge"
        />
      </div>
      <x-button
        class="open-positions-item-mobile__close-button"
        size="3xs"
        variant="tertiary"
        @click="handleClosePosition"
      >
        <ot-icon name="close-square" />
      </x-button>
    </div>
    <div class="open-positions-item-mobile__details">
      <div class="open-positions-item-mobile__column">
        <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">{{ $t('modules.myOrders.header.side') }}:</span>
          <span
            class="open-positions-item-mobile__value"
            :class="{ buy: position.direction === 'LONG', sell: position.direction === 'SHORT' }"
          >
            {{ directionText }}
          </span>
        </div>
        <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">{{ $t('modules.myOrders.header.size') }}:</span>
          <span class="open-positions-item-mobile__value">
            <ot-number-display
              :number="position.open_volume"
              :precision="market?.marketPrecision"
              :suffix="market?.base.code"
            />
          </span>
        </div>
        <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">{{ $t('modules.myOrders.header.averageEntryPrice') }}:</span>
          <span class="open-positions-item-mobile__value">
            <ot-number-display
              :number="position.average_entry_price"
              :precision="market?.marketPrecision"
              :suffix="market?.quote.code"
            />
          </span>
        </div>
        <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">{{ $t('modules.myOrders.header.positionValue') }}:</span>
          <span class="open-positions-item-mobile__value">
            <ot-number-display
              :number="position.open_position"
              :precision="market?.marketPrecision"
              :suffix="market?.quote.code"
            />
          </span>
        </div>
      </div>
      <div class="open-positions-item-mobile__column">
        <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">{{ $t('modules.myOrders.header.markPrice') }}:</span>
          <span class="open-positions-item-mobile__value">
            <ot-number-display
              :number="position.mark_price"
              :precision="market?.marketPrecision"
              :suffix="market?.quote.code"
            />
          </span>
        </div>
        <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">{{ $t('modules.myOrders.header.realisedPnl') }}:</span>
          <span
            class="open-positions-item-mobile__value"
            :class="{ positive: Number(position.realised_pnl) > 0, negative: Number(position.realised_pnl) < 0 }"
          >
            <ot-number-display
              :number="Number(position.realised_pnl)"
              :precision="market?.marketPrecision"
              :suffix="market?.quote.code"
            />
          </span>
        </div>
        <!-- <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">Liq. Price:</span>
          <span class="open-positions-item-mobile__value">
            <ot-number-display
              :number="position.liquidationPrice"
              :precision="market?.marketPrecision"
              :suffix="market?.quote.code"
            />
          </span>
        </div> -->
        <div class="open-positions-item-mobile__row">
          <span class="open-positions-item-mobile__label">{{ $t('modules.myOrders.header.marginUsed') }}:</span>
          <span class="open-positions-item-mobile__value">
            <ot-number-display
              :number="marginUsed"
              :precision="market?.marketPrecision"
              :suffix="market?.quote.code"
            />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */

.open-positions-item-mobile {
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-top: 8px;
  margin-bottom: 8px;
  border: 1px solid rgb(var(--v-theme-elevation-2));
  border-radius: var(--app-border-radius);
  background-color: rgb(var(--v-theme-elevation-1));

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__market-info {
    display: flex;
    align-items: center;
  }

  &__market-name {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: rgb(var(--v-theme-text-primary));
  }

  &__lozenge {
    margin-left: 4px;
  }

  &__close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background-color: transparent;
    color: rgb(var(--v-theme-text-primary));
    cursor: pointer;
  }

  &__details {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }

  &__column {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  &__row {
    display: flex;
    justify-content: space-between;
  }

  &__label {
    font-size: var(--font-size-sm);
    color: rgb(var(--v-theme-text-secondary));
  }

  &__value {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: rgb(var(--v-theme-text-primary));

    &.buy {
      color: rgb(var(--v-theme-success));
    }

    &.sell {
      color: rgb(var(--v-theme-error));
    }

    &.positive {
      color: rgb(var(--v-theme-success));
    }

    &.negative {
      color: rgb(var(--v-theme-error));
    }
  }
}
</style>
